/**
 * DiagramGrid Component
 *
 * The thumbnail grid for the Diagram_Browser (the default view). It lays out a
 * responsive grid of `DiagramCard`s, each of which lazy-loads its own thumbnail
 * as it scrolls into view. See Requirements 9.2 and 9.6.
 */

import type { DiagramSummary } from "@/services/diagramsService"
import DiagramCard from "@/components/diagrams/DiagramCard"

/** Props for the DiagramGrid component. */
interface DiagramGridProps {
  /** The diagrams to display, already filtered and sorted by the parent. */
  diagrams: DiagramSummary[]
  /** Returns a fresh Auth0 access token for the thumbnail requests. */
  getAccessToken: () => Promise<string>
  /** Called when the user opens a diagram in the editor. */
  onOpen: (diagram: DiagramSummary) => void
  /**
   * Renders the actions menu (task 5.4) for a given diagram. Optional so the
   * grid renders before the actions task lands.
   */
  renderActions?: (diagram: DiagramSummary) => React.ReactNode
}

/**
 * Responsive thumbnail grid of diagram cards.
 *
 * @param props - Component props.
 * @param props.diagrams - The diagrams to display (filtered and sorted).
 * @param props.getAccessToken - Token getter passed to each card's thumbnail.
 * @param props.onOpen - Handler to open a diagram in the editor.
 * @param props.renderActions - Optional per-diagram actions menu renderer.
 * @returns The rendered grid element.
 */
export default function DiagramGrid({
  diagrams,
  getAccessToken,
  onOpen,
  renderActions,
}: DiagramGridProps): React.JSX.Element {
  return (
    <div className="h-full overflow-y-auto">
      <ul className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-4 p-0.5">
        {diagrams.map((diagram) => (
          <li key={diagram.id}>
            <DiagramCard
              diagram={diagram}
              getAccessToken={getAccessToken}
              onOpen={onOpen}
              actionsSlot={renderActions ? renderActions(diagram) : undefined}
            />
          </li>
        ))}
      </ul>
    </div>
  )
}
